import React from 'react';
import { useDrag } from 'react-dnd';
import {
  DocumentTextIcon,
  AtSymbolIcon,
  PhoneIcon,
  HashtagIcon,
  CurrencyDollarIcon,
  CalendarDaysIcon,
  ClockIcon,
  ChatBubbleBottomCenterTextIcon,
  ListBulletIcon,
  CheckCircleIcon,
  RadioIcon,
  DocumentArrowUpIcon,
  PhotoIcon,
  SwatchIcon,
  AdjustmentsHorizontalIcon,
  StarIcon,
  PencilIcon,
  MapPinIcon,
  UserIcon,
  CubeIcon,
  BuildingOfficeIcon,
  UsersIcon,
  SparklesIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline';
import { FieldType, FormField } from '../../types/formBuilder';
import { useLanguage } from '../../contexts/LanguageContext';
import { createFieldFromType } from './fieldUtils';

interface FieldPaletteProps {
  onAddField: (field: FormField) => void;
}

interface FieldTypeConfig {
  type: FieldType;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  category: 'basic' | 'advanced' | 'lookup' | 'special';
  description: string;
}

// Helper function to get field type configurations with translations
const getFieldTypes = (t: (key: string) => string): FieldTypeConfig[] => [
  // Basic Fields
  { type: 'text', label: t('formBuilder.fieldTypes.text'), icon: DocumentTextIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.text') },
  { type: 'email', label: t('formBuilder.fieldTypes.email'), icon: AtSymbolIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.email') },
  { type: 'phone', label: t('formBuilder.fieldTypes.phone'), icon: PhoneIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.phone') },
  { type: 'number', label: t('formBuilder.fieldTypes.number'), icon: HashtagIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.number') },
  { type: 'currency', label: t('formBuilder.fieldTypes.currency'), icon: CurrencyDollarIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.currency') },
  { type: 'date', label: t('formBuilder.fieldTypes.date'), icon: CalendarDaysIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.date') },
  { type: 'time', label: t('formBuilder.fieldTypes.time'), icon: ClockIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.time') },
  { type: 'textarea', label: t('formBuilder.fieldTypes.textarea'), icon: ChatBubbleBottomCenterTextIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.textarea') },
  
  // Selection Fields
  { type: 'select', label: t('formBuilder.fieldTypes.select'), icon: ListBulletIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.select') },
  { type: 'multiselect', label: t('formBuilder.fieldTypes.multiselect'), icon: CheckCircleIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.multiselect') },
  { type: 'radio', label: t('formBuilder.fieldTypes.radio'), icon: RadioIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.radio') },
  { type: 'checkbox', label: t('formBuilder.fieldTypes.checkbox'), icon: CheckCircleIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.checkbox') },
  { type: 'switch', label: t('formBuilder.fieldTypes.switch'), icon: AdjustmentsHorizontalIcon, category: 'basic', description: t('formBuilder.fieldDescriptions.switch') },
  
  // Advanced Fields
  { type: 'file', label: t('formBuilder.fieldTypes.file'), icon: DocumentArrowUpIcon, category: 'advanced', description: t('formBuilder.fieldDescriptions.file') },
  { type: 'image', label: t('formBuilder.fieldTypes.image'), icon: PhotoIcon, category: 'advanced', description: t('formBuilder.fieldDescriptions.image') },
  { type: 'color', label: t('formBuilder.fieldTypes.color'), icon: SwatchIcon, category: 'advanced', description: t('formBuilder.fieldDescriptions.color') },
  { type: 'range', label: t('formBuilder.fieldTypes.range'), icon: AdjustmentsHorizontalIcon, category: 'advanced', description: t('formBuilder.fieldDescriptions.range') },
  { type: 'rating', label: t('formBuilder.fieldTypes.rating'), icon: StarIcon, category: 'advanced', description: t('formBuilder.fieldDescriptions.rating') },
  { type: 'signature', label: t('formBuilder.fieldTypes.signature'), icon: PencilIcon, category: 'advanced', description: t('formBuilder.fieldDescriptions.signature') },
  { type: 'address', label: t('formBuilder.fieldTypes.address'), icon: MapPinIcon, category: 'advanced', description: t('formBuilder.fieldDescriptions.address') },
  
  // Lookup Fields
  { type: 'customer_lookup', label: t('formBuilder.fieldTypes.customer_lookup'), icon: UserIcon, category: 'lookup', description: t('formBuilder.fieldDescriptions.customer_lookup') },
  { type: 'product_lookup', label: t('formBuilder.fieldTypes.product_lookup'), icon: CubeIcon, category: 'lookup', description: t('formBuilder.fieldDescriptions.product_lookup') },
  { type: 'user_lookup', label: t('formBuilder.fieldTypes.user_lookup'), icon: UsersIcon, category: 'lookup', description: t('formBuilder.fieldDescriptions.user_lookup') },
  { type: 'organization_lookup', label: t('formBuilder.fieldTypes.organization_lookup'), icon: BuildingOfficeIcon, category: 'lookup', description: t('formBuilder.fieldDescriptions.organization_lookup') },
];

const FieldPalette: React.FC<FieldPaletteProps> = ({ onAddField }) => {
  const { t } = useLanguage();

  const fieldTypes = getFieldTypes(t);
  
  const categories = [
    { key: 'basic', label: t('formBuilder.categories.basic') || 'Basic Fields' },
    { key: 'advanced', label: t('formBuilder.categories.advanced') || 'Advanced Fields' },
    { key: 'lookup', label: t('formBuilder.categories.lookup') || 'Lookup Fields' },
  ];

  return (
    <div className="h-full flex flex-col bg-gradient-to-br from-purple-50 via-white to-indigo-50 dark:from-gray-800 dark:via-gray-900 dark:to-gray-800">
      {/* Header */}
      <div className="flex-shrink-0 p-4 bg-gradient-to-r from-purple-500 to-purple-600 text-white">
        <div className="flex items-center space-x-3">
          <div className="w-8 h-8 bg-white/20 backdrop-blur-sm rounded-lg flex items-center justify-center">
            <RectangleStackIcon className="h-5 w-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-semibold">
              {t('formBuilder.fieldPalette') || 'Field Palette'}
            </h3>
            <p className="text-purple-100 text-xs">
              {t('formBuilder.fieldPaletteDescription') || 'Drag fields to canvas or click to add'}
            </p>
          </div>
        </div>
      </div>
      
      {/* Scrollable Content */}
      <div className="flex-1 min-h-0 overflow-y-auto p-4 pb-8 space-y-4">
        {categories.map(category => (
          <div key={category.key} className="bg-white/60 backdrop-blur-sm dark:bg-gray-800/60 rounded-xl p-4 shadow-lg border border-white/20">
            <div className="flex items-center space-x-2 mb-3">
              <div className="w-6 h-6 bg-gradient-to-r from-purple-400 to-purple-500 rounded-md flex items-center justify-center">
                <SparklesIcon className="h-3 w-3 text-white" />
              </div>
              <h4 className="text-sm font-semibold text-gray-900 dark:text-white uppercase tracking-wide">
                {category.label}
              </h4>
            </div>
            
            <div className="space-y-2">
              {fieldTypes
                .filter(field => field.category === category.key)
                .map(fieldType => (
                  <DraggableField
                    key={fieldType.type}
                    fieldType={fieldType}
                    onAddField={onAddField}
                  />
                ))}
            </div>
          </div>
        ))}
        
        {/* Bottom spacer to ensure last items are fully visible */}
        <div className="h-12"></div>
      </div>
    </div>
  );
};

interface DraggableFieldProps {
  fieldType: FieldTypeConfig;
  onAddField: (field: FormField) => void;
}

const DraggableField: React.FC<DraggableFieldProps> = ({ fieldType, onAddField }) => {
  const { t } = useLanguage();
  const [{ isDragging }, drag] = useDrag({
    type: 'FIELD',
    item: { fieldType },
    collect: (monitor: any) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const handleClick = () => {
    const newField = createFieldFromType(fieldType.type, t);
    onAddField(newField);
  };

  return (
    <div
      ref={drag}
      onClick={handleClick}
      className={`
        bg-gradient-to-r from-white to-gray-50 dark:from-gray-700 dark:to-gray-600 
        border border-gray-200 dark:border-gray-600 rounded-lg cursor-pointer 
        hover:from-purple-50 hover:to-purple-100 dark:hover:from-gray-600 dark:hover:to-gray-500
        hover:border-purple-300 dark:hover:border-purple-500
        transition-all duration-200 group transform hover:scale-105 hover:shadow-md
        ${isDragging ? 'opacity-50 scale-95' : ''}
      `}
      style={{ padding: '0.65rem' }}
    >
      <div className="flex items-center space-x-3">
        <div className="w-8 h-8 bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-800 dark:to-purple-700 rounded-lg flex items-center justify-center group-hover:from-purple-200 group-hover:to-purple-300 dark:group-hover:from-purple-700 dark:group-hover:to-purple-600 transition-all">
          <fieldType.icon className="h-4 w-4 text-purple-600 dark:text-purple-300 group-hover:text-purple-700 dark:group-hover:text-purple-200" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-xs font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-purple-900 dark:group-hover:text-purple-100">
            {fieldType.label}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 truncate group-hover:text-purple-600 dark:group-hover:text-purple-300">
            {fieldType.description}
          </p>
        </div>
      </div>
    </div>
  );
};


export default FieldPalette;
